<template>
  <!--如果q-input作为顶层节点, 则其html标签的QInput(Vue对象)信息将被抹去, 故使用div包装-->
  <div class="i-field-datetime">
    <q-input ref="input" :value="displayValue" :dense="computedDense" v-bind="computedAttrs" v-on="computedListeners">
      <template v-slot:prepend>
        <q-btn icon="event" round dense flat :color="prependIconColor" :tabindex="-1" :disable="disable || readonly" @click="focusInput" :glossy="false">
          <q-tooltip anchor="center right" self="center left" :offset="[10, 10]">选择日期</q-tooltip>
          <q-popup-proxy transition-show="scale" transition-hide="scale" :target="proxyTarget" @before-show="focusInput">
            <div style="overflow:hidden" ref="datePanel">
              <q-date ref="inputDate" :value="displayValue" @input="onDatetimeInput" :mask="computedDisplayFormat" :class="format === 'birthMonthDay' ? 'hideDateYear' : ''">
                <div class="row items-center justify-end">
                  <q-btn :icon="$q.iconSet.datetime.today" flat label="今天" @click="$refs.inputDate.setToday()" color="primary" />
                  <q-space />
                  <q-btn v-close-popup v-if="displayValue" label="确定" icon="check_circle" color="positive" flat />
                  <q-btn v-close-popup v-else label="取消" icon="cancel" color="red" flat />
                </div>
              </q-date>
            </div>
          </q-popup-proxy>
        </q-btn>
      </template>

      <template v-slot:append v-if="requiresTime && !(disable || readonly)">
        <q-btn icon="access_time" round dense flat :color="prependIconColor" :tabindex="-1" :disable="disable || readonly" @click="focusInput" :glossy="false">
          <q-tooltip anchor="center left" self="center right" :offset="[10, 10]">选择时间</q-tooltip>
          <!--:target="proxyTarget", 不能同时设置Date和Time，否则会同时弹出-->
          <q-popup-proxy transition-show="scale" transition-hide="scale" @before-show="focusInput">
            <q-time ref="inputTime" :value="displayValue" @input="onDatetimeInput" :mask="computedDisplayFormat" format24h>
              <div class="row items-center justify-end" ref="timePanel">
                <q-btn :icon="$q.iconSet.datetime.now" flat label="现在" @click="$refs.inputTime.setNow()" color="primary" />
                <q-space />
                <q-btn v-close-popup v-if="displayValue" label="确定" icon="check_circle" color="positive" flat />
                <q-btn v-close-popup v-else label="取消" icon="cancel" color="red" flat />
              </div>
            </q-time>
          </q-popup-proxy>
        </q-btn>
      </template>
    </q-input>
  </div>
</template>
<script>
import moment from 'moment'
import rules from '@/lib/rules.js'
import utils from '@/lib/utils'
import _ from 'lodash'
import FieldIconMixin from './FieldIconMixin.js'
import FieldProxyMixin from './FieldProxyMixin.js'
import FieldDenseMixin from './FieldDenseMixin'
// import utils from '@/lib/utils'

const formats = {
  birthMonthDay: 'MM-DD',
  date: 'YYYY-MM-DD',
  default: 'YYYY-MM-DD HH:mm',
  datetime: 'YYYY-MM-DD HH:mm:ss',
  iso: 'YYYY-MM-DDTHH:mm:ss.SSSZ'
}

const formatList = _.values(formats)

const overrideProps = {
  // displayFormat
  format: {
    type: String,
    default: formats.default
  },
  value: [String, Date],
  valueFormat: {
    type: String,
    // 由于BIS系统后台框架限制, 传给后台的时间日期格式默认统一为此格式
    default: formats.datetime
  },
  rules: Array,
  disable: Boolean,
  readonly: Boolean,
  endOfDay: Boolean,
  startOfDay: Boolean
}

export default {
  data () {
    return {
      mountedFlag: false
    }
  },
  props: overrideProps,
  mixins: [FieldIconMixin, FieldProxyMixin, FieldDenseMixin],
  computed: {
    proxyTarget () {
      return this.disable || !this.mountedFlag ? undefined : this.$refs.input
    },
    requiresTime () {
      return this.computedDisplayFormat.search(/[HmsS]/) !== -1
    },
    computedValueFormat () {
      return formats[this.valueFormat] ?? this.valueFormat
    },
    computedDisplayFormat () {
      return formats[this.format] ?? this.format
    },
    displayValue () {
      return this.formatValue(this.value, this.computedDisplayFormat)
    },
    displayMask () {
      // defaultFormat: YYYY-MM-DDTHH:mm:ss.SSSZ
      return this.computedDisplayFormat?.replace(/[YMDHmsS]/g, '#')
    },
    computedRules () {
      const computedRules = [
        // [this.computedDisplayFormat, moment.ISO_8601]
        rules.datetimeWith(formatList)
      ]
      if (this.rules) {
        Array.prototype.push.apply(computedRules, this.rules)
      }
      return computedRules
    },
    computedAttrs () {
      const attrs = Object.assign({
        disable: false,
        readonly: this.readonly || this.disable,
        // 'fill-mask': true,
        mask: this.displayMask,
        clearable: !(this.disable || this.readonly),
        clearIcon: 'close',
        rules: this.computedRules,
        'lazy-rules': true,
        'no-error-icon': true,
        'label-color': 'black'
      }, this.$attrs, _.omit(this.$props, Object.keys(overrideProps)))
      return attrs
    },
    computedListeners () {
      const listeners = Object.assign({}, this.$listeners, {
        input: (...args) => {
          this.onDatetimeInput(...args)
        }
      })
      return listeners
    }
  },
  mounted () {
    this.mountedFlag = true
    utils.onActiveElementChanged(this.detectInputFocusChanged)
  },
  beforeDestroy () {
    this.mountedFlag = false
    utils.offActiveElementChanged(this.detectInputFocusChanged)
  },
  watch: {
    value: {
      immediate: true,
      handler () {
        if ((this.startOfDay || this.endOfDay) && this.value) {
          const computedValue = this.formatValue(this.value, this.computedValueFormat)
          const modifiedValue = this.formatValue(this.value, this.computedValueFormat, this.startOfDay, this.endOfDay)
          if (modifiedValue !== computedValue) {
            this.$emit('input', modifiedValue)
          }
        }
      }
    }
  },
  methods: {
    formatValue (value, format, startOfDay, endOfDay) {
      // bug: 输入 1993 自动变成 1993-3
      // 优化: 用户输入 93727 -> 1993-07-27
      if (value) {
        let momentVal = moment(value, formatList, true)
        if (momentVal.isValid()) {
          momentVal = startOfDay ? momentVal.startOf('day') : (endOfDay ? momentVal.endOf('day') : momentVal)
          return momentVal.format(format)
        } else {
          return value
        }
      } else {
        return '' // 不能返回null, 只能返回空字符串, 否则quasar input组件有问题
      }
    },
    onDatetimeInput (...args) {
      const formatMethod = this.startOfDay ? 'formatStartOfDay' : (this.endOfDay ? 'formatEndOfDay' : 'formatDate')
      const input = utils[formatMethod](args[0], this.computedValueFormat, this.computedDisplayFormat, true)
      this.$emit('input', input, ...args.slice(1))
    },
    focusInput () {
      setTimeout(() => {
        this.$refs.input && this.$refs.input.focus()
      }, 0)
    },
    detectInputFocusChanged () {
      if (utils.isAncestorElementOf(this.$refs.datePanel, document.activeElement)) {
        this.$refs.input && this.$refs.input.focus()
      } else if (utils.isAncestorElementOf(this.$refs.timePanel, document.activeElement)) {
        this.$refs.input && this.$refs.input.focus()
      }
    }
  }
}
</script>
<style>
.hideDateYear .q-date__navigation >div:nth-child(4){
  display: none;
}
.hideDateYear .q-date__navigation >div:nth-child(5){
  display: none;
}
.hideDateYear .q-date__navigation >div:nth-child(6){
  display: none;
}
</style>
